import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { VisitDayDto, VisitDto, VisitEventDto } from "@klantpunt/shared";
import { api, ApiError } from "../../lib/api";
import { hasModule, useMe } from "../../lib/auth";

export const visitsKey = (buildingId: string) => ["visits", buildingId] as const;
export const errMsg = (e: unknown) => (e instanceof ApiError || e instanceof Error ? e.message : "Er ging iets mis");

export interface VisitPerson {
  id: string;
  name: string;
  role: string;
}

/** Bezoek van één dag in een gebouw, plus wie er nu binnen is en open bezoek van eerdere dagen. */
export function useVisitDay(buildingId: string, day: string | null) {
  const { data: me } = useMe();
  return useQuery({
    queryKey: [...visitsKey(buildingId), "dag", day ?? "vandaag"],
    queryFn: () => api<VisitDayDto>(`/buildings/${buildingId}/visits${day ? `?dag=${day}` : ""}`),
    enabled: !!buildingId && hasModule(me, "visits"),
    // De lijst Te controleren hangt af van de klok.
    refetchInterval: 60_000,
  });
}

export function useVisitEvents(visitId: string) {
  return useQuery({
    queryKey: ["visit", visitId, "events"],
    queryFn: () => api<VisitEventDto[]>(`/visits/${visitId}/events`),
  });
}

/** Collega's met toegang tot het gebouw: gastheer, begeleider of eigen dienst. */
export function useVisitPeople(buildingId: string) {
  return useQuery({
    queryKey: [...visitsKey(buildingId), "people"],
    queryFn: () => api<VisitPerson[]>(`/buildings/${buildingId}/visit-people`),
    staleTime: 60_000,
  });
}

/** Na een wijziging: lijst en logboek opnieuw ophalen. */
export function useVisitsRefresh(buildingId: string) {
  const qc = useQueryClient();
  return (v?: VisitDto) =>
    Promise.all([
      qc.invalidateQueries({ queryKey: visitsKey(buildingId) }),
      v ? qc.invalidateQueries({ queryKey: ["visit", v.id] }) : null,
    ]);
}
